import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Lock, Send, BarChart2, ArrowUpRight, Users } from 'lucide-react';

const CAMPAIGN_STATS = [
  { label: 'Total Sent', icon: Send, iconColor: 'text-blue-500', iconBg: 'bg-blue-50' },
  { label: 'Open Rate', icon: BarChart2, iconColor: 'text-purple-500', iconBg: 'bg-purple-50' },
  { label: 'Conversions', icon: ArrowUpRight, iconColor: 'text-[#00D95F]', iconBg: 'bg-green-50' },
];

const TEMPLATES = [
  { name: 'Giri Universal Handler', id: 'giri_universal_handler' },
  { name: 'Welcome Prospect', id: 'welcome_prospect' },
  { name: 'Qualified Follow-up', id: 'qualified_followup' },
  { name: 'Re-engagement Nudge', id: 'reengage_nudge' },
];

const LEAD_STAGES = ['Prospect', 'Aware', 'Engaged', 'Qualified', 'Won'];

export function MarketingPage() {
  return (
    <div className="space-y-8" data-testid="marketing-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Campaign Builder</h2>
          <p className="text-sm text-slate-500 mt-1">
            Create and send WhatsApp marketing campaigns to your leads
          </p>
        </div>
        <span className="shrink-0 text-xs font-bold bg-amber-100 text-amber-700 px-3 py-1 rounded-full">
          Beta
        </span>
      </div>

      {/* Campaign Stats — skeleton state */}
      <section>
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-4">
          Campaign Overview
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {CAMPAIGN_STATS.map(({ label, icon: Icon, iconColor, iconBg }) => (
            <div
              key={label}
              className="bg-white rounded-xl border border-gray-100 shadow-sm p-5"
              data-testid={`campaign-stat-${label.toLowerCase().replace(/ /g, '-')}`}
            >
              <div className="flex items-center justify-between mb-4">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">
                  {label}
                </p>
                <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center`}>
                  <Icon className={`w-4 h-4 ${iconColor}`} />
                </div>
              </div>
              <Skeleton className="h-8 w-20 mb-2" />
              <Skeleton className="h-3 w-28" />
            </div>
          ))}
        </div>
      </section>

      {/* Audience Filter */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-sm font-semibold text-slate-900">Target Audience</h3>
          <div className="flex items-center gap-1.5 text-slate-400">
            <Users className="w-4 h-4" />
            <span className="text-xs">— contacts selected</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-2">
              Lead Stage
            </label>
            <Select disabled>
              <SelectTrigger
                data-testid="audience-stage-select"
                className="bg-gray-50 border-gray-200"
              >
                <SelectValue placeholder="Filter by lead stage..." />
              </SelectTrigger>
              <SelectContent>
                {LEAD_STAGES.map((s) => (
                  <SelectItem key={s} value={s.toLowerCase()}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-2">
              Seller ID
            </label>
            <Select disabled>
              <SelectTrigger
                data-testid="audience-seller-select"
                className="bg-gray-50 border-gray-200"
              >
                <SelectValue placeholder="Select seller..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sellers</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Template Selector */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-4">Select WhatsApp Template</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {TEMPLATES.map(({ name, id }) => (
            <div
              key={id}
              data-testid={`template-card-${id}`}
              className="flex items-center justify-between p-4 rounded-lg border border-gray-100 bg-gray-50 opacity-70 cursor-not-allowed select-none"
            >
              <div>
                <p className="text-sm font-medium text-slate-700">{name}</p>
                <p className="text-xs text-slate-400 mt-0.5 font-mono">giri_universal_handler</p>
              </div>
              <Lock className="w-4 h-4 text-slate-400 shrink-0 ml-3" />
            </div>
          ))}
        </div>
      </div>

      {/* Beta CTA */}
      <div className="bg-[#F0FDFB] border border-[#00D1B2]/25 rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <p className="font-semibold text-slate-900">Get early access to Campaign Builder</p>
          <p className="text-sm text-slate-500 mt-1">
            Full launch coming soon. Request beta access to be among the first.
          </p>
        </div>
        <Button
          data-testid="request-beta-btn"
          className="shrink-0 bg-[#00D1B2] hover:bg-[#00B89C] text-white font-semibold"
        >
          Request Beta Access
        </Button>
      </div>
    </div>
  );
}
